<template>
  <div class="dashboard-metrics">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>
            Overview
          </h2>
        </div>
      </div>
    </div>


    <div class="row mb-3">
      <div class="col-12">
        <div class="btn-group" role="group">
          <button v-for="tf in timeframes" :key="tf.value" type="button" 
                  :class="['btn', timeframe === tf.value ? 'btn-primary' : 'btn-outline-primary']"
                  @click="selectTimeframe(tf.value)">
            {{ tf.label }}
          </button>
        </div>
        <button class="btn btn-outline-secondary ms-2" @click="refreshMetrics" :disabled="loading">
          <i class="fa-solid fa-refresh" :class="{'fa-spin': loading}"></i> Refresh
        </button>
      </div>
    </div>

    <div v-if="loading && !metrics" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else-if="metrics">
      <!-- Core Metrics KPI Cards -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1">Total Rides</p>
                  <h3 class="mb-0">{{ formatNumber(metrics.core.totalRides) }}</h3>
                  <span :class="getChangeClass(metrics.core.totalRidesChange)">
                    <i :class="getChangeIcon(metrics.core.totalRidesChange)"></i>
                    {{ formatPercentage(metrics.core.totalRidesChange) }}%
                  </span>
                </div>
                <div class="stats-icon bg-primary">
                  <i class="fa-solid fa-car"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1">Total Revenue</p>
                  <h3 class="mb-0">{{ formatCurrency(metrics.core.totalRevenue) }}</h3>
                  <span :class="getChangeClass(metrics.core.totalRevenueChange)">
                    <i :class="getChangeIcon(metrics.core.totalRevenueChange)"></i>
                    {{ formatPercentage(metrics.core.totalRevenueChange) }}%
                  </span>
                </div>
                <div class="stats-icon bg-success">
                  <i class="fa-solid fa-dollar-sign"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1">Active Rides</p>
                  <h3 class="mb-0">{{ formatNumber(metrics.core.activeRides) }}</h3>
                  <span class="text-info">
                    <i class="fa-solid fa-circle-dot"></i> Live
                  </span>
                </div>
                <div class="stats-icon bg-info">
                  <i class="fa-solid fa-road"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-3 col-sm-6 mb-3">
          <div class="card stats-card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="text-muted mb-1">Completion Rate</p>
                  <h3 class="mb-0">{{ formatPercentage(metrics.core.completionRate) }}%</h3>
                  <span :class="getChangeClass(metrics.core.completionRateChange)">
                    <i :class="getChangeIcon(metrics.core.completionRateChange)"></i>
                    {{ formatPercentage(metrics.core.completionRateChange) }}%
                  </span>
                </div>
                <div class="stats-icon bg-warning">
                  <i class="fa-solid fa-check-circle"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="row mb-4">
        <div class="col-lg-8 mb-3">
          <div class="card">
            <div class="card-header">
              <h5>Revenue Trend</h5>
            </div>
            <div class="card-body">
              <canvas ref="revenueChart"></canvas>
            </div>
          </div>
        </div>

        <div class="col-lg-4 mb-3">
          <div class="card">
            <div class="card-header">
              <h5>Ride Status</h5>
            </div>
            <div class="card-body">
              <canvas ref="statusChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Driver and Operations Metrics -->
      <div class="row mb-4">
        <div class="col-lg-6 mb-3">
          <div class="card">
            <div class="card-header">
              <h5>Driver Metrics</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 mb-3">
                  <div class="metric-item">
                    <div class="metric-value">{{ formatNumber(metrics.driver.activeDrivers) }}</div>
                    <div class="metric-label">Active Drivers</div>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-item">
                    <div class="metric-value">{{ formatNumber(metrics.driver.totalDrivers) }}</div>
                    <div class="metric-label">Total Drivers</div>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-item">
                    <div class="metric-value text-warning">{{ formatNumber(metrics.driver.driversNeedingVerification) }}</div>
                    <div class="metric-label">Pending Verification</div>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-item">
                    <div class="metric-value">{{ formatPercentage(metrics.driver.utilizationRate) }}%</div>
                    <div class="metric-label">Utilization Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6 mb-3">
          <div class="card">
            <div class="card-header">
              <h5>Operational Metrics</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 mb-3">
                  <div class="metric-item">
                    <div class="metric-value">{{ formatNumber(metrics.operations.cancellations.totalCancelled) }}</div>
                    <div class="metric-label">Total Cancellations</div>
                  </div>
                </div>
                <div class="col-6 mb-3">
                  <div class="metric-item">
                    <div class="metric-value">{{ formatNumber(metrics.operations.averageWaitTimeMinutes) }} min</div>
                    <div class="metric-label">Avg Wait Time</div>
                  </div>
                </div>
                <div class="col-12">
                  <h6 class="mb-2">Cancellation Breakdown</h6>
                  <div class="cancellation-stats">
                    <div class="d-flex justify-content-between mb-1">
                      <span>By Rider:</span>
                      <strong>{{ formatNumber(metrics.operations.cancellations.cancelledByRider) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                      <span>By Driver:</span>
                      <strong>{{ formatNumber(metrics.operations.cancellations.cancelledByDriver) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span>By Dispatch:</span>
                      <strong>{{ formatNumber(metrics.operations.cancellations.cancelledByDispatch) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Peak Hours Chart -->
      <div class="row" v-if="metrics.operations.peakHours && metrics.operations.peakHours.length">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5>Peak Hours Activity</h5>
            </div>
            <div class="card-body">
              <canvas ref="peakHoursChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import { Chart, registerables } from 'chart.js';
import {driverStatus} from "../utility/DatatableRenderer";

Chart.register(...registerables);

const props = defineProps({
  serviceProviderId: {
    type: String,
    required: true
  }
});

const timeframes = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' }
];

const timeframe = ref('week');
const metrics = ref(null);
const loading = ref(false);
const error = ref(null);

const revenueChart = ref(null);
const statusChart = ref(null);
const peakHoursChart = ref(null);

let revenueChartInstance = null;
let statusChartInstance = null;
let peakHoursChartInstance = null;

const fetchMetrics = async (refresh = false) => {
  loading.value = true;
  error.value = null;
  
  try {
    const url = `/api/dashboard/metrics/${props.serviceProviderId}?timeframe=${timeframe.value}&refresh=${refresh}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch dashboard metrics');
    }
    
    metrics.value = await response.json();
    await nextTick();
    renderCharts();
  } catch (err) {
    error.value = err.message;
    console.error('Error fetching dashboard metrics:', err);
  } finally {
    loading.value = false;
  }
};

const selectTimeframe = (tf) => {
  timeframe.value = tf;
  fetchMetrics();
};

const refreshMetrics = () => {
  fetchMetrics(true);
};

const renderCharts = () => {
  if (!metrics.value) return;
  
  renderRevenueChart();
  renderStatusChart();
  renderPeakHoursChart();
};

const renderRevenueChart = () => {
  if (!revenueChart.value || !metrics.value.trends.revenue) return;
  
  if (revenueChartInstance) {
    revenueChartInstance.destroy();
  }
  
  const ctx = revenueChart.value.getContext('2d');
  revenueChartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: metrics.value.trends.revenue.map(p => p.label),
      datasets: [
        {
          label: 'Revenue',
          data: metrics.value.trends.revenue.map(p => p.revenue),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Rides',
          data: metrics.value.trends.rides.map(p => p.count),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Revenue ($)'
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Number of Rides'
          },
          grid: {
            drawOnChartArea: false
          }
        }
      }
    }
  });
};

const renderStatusChart = () => {
  if (!statusChart.value || !metrics.value.trends.rideStatusBreakdown) return;
  
  if (statusChartInstance) {
    statusChartInstance.destroy();
  }
  
  const ctx = statusChart.value.getContext('2d');
  const statusData = metrics.value.trends.rideStatusBreakdown;
  
  statusChartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: statusData.map(s => s.status.replace(/_/g, ' ')),
      datasets: [{
        data: statusData.map(s => s.count),
        backgroundColor: [
          'rgb(75, 192, 192)',
          'rgb(255, 205, 86)',
          'rgb(255, 99, 132)',
          'rgb(54, 162, 235)',
          'rgb(153, 102, 255)',
          'rgb(201, 203, 207)'
        ]
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  });
};

const renderPeakHoursChart = () => {
  if (!peakHoursChart.value || !metrics.value.operations.peakHours) return;
  
  if (peakHoursChartInstance) {
    peakHoursChartInstance.destroy();
  }
  
  const ctx = peakHoursChart.value.getContext('2d');
  
  peakHoursChartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: metrics.value.operations.peakHours.map(p => p.hour),
      datasets: [{
        label: 'Ride Count',
        data: metrics.value.operations.peakHours.map(p => p.rideCount),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Number of Rides'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Hour of Day'
          }
        }
      }
    }
  });
};

const formatNumber = (num) => {
  return num != null ? num.toLocaleString() : '0';
};

const formatCurrency = (amount) => {
  return amount != null ? `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';
};

const formatPercentage = (value) => {
  return value != null ? parseFloat(value).toFixed(1) : '0.0';
};

const getChangeClass = (change) => {
  if (change > 0) return 'text-success';
  if (change < 0) return 'text-danger';
  return 'text-muted';
};

const getChangeIcon = (change) => {
  if (change > 0) return 'fa-solid fa-arrow-up';
  if (change < 0) return 'fa-solid fa-arrow-down';
  return 'fa-solid fa-minus';
};

onMounted(() => {
  fetchMetrics();
  
  // Refresh every 60 seconds
  setInterval(() => {
    fetchMetrics();
  }, 60000);
});
</script>

<style scoped>
.stats-card {
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.stats-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.metric-item {
  text-align: center;
  padding: 10px;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
}

.metric-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 5px;
}

.cancellation-stats {
  font-size: 0.9rem;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
}

canvas {
  height: 300px !important;
}

.card {
  border: none;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-header {
  background-color: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

.card-header h5 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}
</style>
